import React, { useCallback, useEffect, useState } from "react";
import { useConfirm } from "./ConfirmModal";
import Icons from "../icons";
import HelpButton from "./HelpDialog";
import CreateReportWizard from "./CreateReportWizard";
import AnalyticsView from "./AnalyticsView";
import HistoryView from "./HistoryView";
import TraceabilityView from "./TraceabilityView";
import { useTheme } from "../theme";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/* ------------------------------------------------------------------ */
/*  Sign-off modal (Part 11 e-signature)                               */
/* ------------------------------------------------------------------ */
function SignReportModal({ report, defaultUserid, onClose, onSigned }) {
  const [userid, setUserid] = useState(defaultUserid || "");
  const [password, setPassword] = useState("");
  const [meaning, setMeaning] = useState("Approved");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!userid.trim() || !password) return;
    setSaving(true);
    setErr("");
    try {
      const res = await fetch(
        `${API_BASE_URL}reports/${encodeURIComponent(report.reportkey)}/sign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userid: userid.trim(), password, meaning }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      onSigned();
    } catch (e) {
      setErr(e.message || "Could not sign report.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex w-full max-w-md flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">Electronic signature</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-600/20">
            Signing is a legally binding act. Re-enter your credentials to confirm.
            Once signed, <b>{report.name}</b> is locked and cannot be edited or deleted.
          </p>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">User ID</label>
            <input value={userid} onChange={(e) => setUserid(e.target.value)} className={inputCls} autoComplete="username" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoComplete="current-password" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Meaning</label>
            <select value={meaning} onChange={(e) => setMeaning(e.target.value)} className={inputCls}>
              <option>Approved</option>
              <option>Reviewed</option>
              <option>Authored</option>
              <option>Verified</option>
            </select>
          </div>
          {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
        </div>
        <div className="flex flex-none justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !userid.trim() || !password}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {saving ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}
            Sign report
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Export menu (PDF / JSON / HTML)                                    */
/* ------------------------------------------------------------------ */
function ExportMenu({ open, onToggle, onExport }) {
  const btnRef = React.useRef(null);
  const [coords, setCoords] = React.useState({ top: 0, left: 0 });

  // Position the menu with fixed coordinates from the trigger's rect. Fixed
  // positioning escapes the table's overflow clipping (overflow-x-auto also
  // clips vertically), so the dropdown is never hidden behind the grid.
  React.useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const MENU_W = 128; // w-32
      setCoords({
        top: r.bottom + 4,
        left: Math.max(8, r.right - MENU_W),
      });
    }
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
      >
        <Icons.Download className="h-3.5 w-3.5" />
        Export
        <Icons.ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <>
          {/* Click-away backdrop. */}
          <div className="fixed inset-0 z-40" onClick={onToggle} />
          <div
            className="fixed z-50 w-32 overflow-hidden rounded-lg border border-slate-200 bg-card shadow-lg"
            style={{ top: coords.top, left: coords.left }}
          >
            {["pdf", "json", "html"].map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => onExport(fmt)}
                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                {fmt.toUpperCase()}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Reports table (grid)                                               */
/* ------------------------------------------------------------------ */
function ReportsTable({
  reports,
  selected,
  onToggleSelect,
  onToggleSelectAll,
  onExport,
  onSign,
  onDelete,
  openMenuKey,
  setOpenMenuKey,
}) {
  const allSelected = reports.length > 0 && reports.every((r) => selected.has(r.reportkey));
  const someSelected = reports.some((r) => selected.has(r.reportkey));

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-card shadow-sm">
      <table className="min-w-full text-left">
        <thead>
          <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <th className="w-10 px-4 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allSelected && someSelected;
                }}
                onChange={onToggleSelectAll}
                aria-label="Select all reports"
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
            </th>
            <th className="px-4 py-3">Report</th>
            <th className="px-4 py-3">Execution</th>
            <th className="px-4 py-3">Env / Build</th>
            <th className="px-4 py-3">Tests</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => {
            const s = report.summary || {};
            const total = s.total ?? (report.testcase_keys || []).length;
            const isSelected = selected.has(report.reportkey);
            return (
              <tr
                key={report.reportkey}
                className={[
                  "border-t border-slate-100 transition",
                  isSelected ? "bg-brand-50/50" : "hover:bg-slate-50",
                ].join(" ")}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(report.reportkey)}
                    aria-label={`Select ${report.name}`}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-brand-600 to-brand-800 text-white">
                      <Icons.Report className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{report.name}</span>
                        {report.signed && (
                          <span
                            title={`Signed by ${report.signed_by_name || ""}`}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-600/20"
                          >
                            <Icons.Check className="h-3 w-3" />
                            Signed
                          </span>
                        )}
                      </div>
                      {report.description && (
                        <div className="truncate text-xs text-slate-500">{report.description}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{report.execution_name || report.board_name || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  <div>{report.environment || "—"}</div>
                  <div>{report.build_label || report.build_id || ""}</div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {total}
                  <span className="ml-1 text-xs text-slate-400">
                    ({s.passed ?? 0}✓ {s.failed ?? 0}✗)
                  </span>
                </td>
                <td className="px-4 py-3">
                  {report.signed ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <Icons.Check className="h-3.5 w-3.5" /> Locked
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">Draft</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-slate-600">{formatDate(report.createddate)}</div>
                  {report.created_by && (
                    <div className="text-xs text-slate-400">By {report.created_by}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <ExportMenu
                      open={openMenuKey === report.reportkey}
                      onToggle={() =>
                        setOpenMenuKey(openMenuKey === report.reportkey ? null : report.reportkey)
                      }
                      onExport={(fmt) => {
                        setOpenMenuKey(null);
                        onExport(report, fmt);
                      }}
                    />
                    {!report.signed && (
                      <button
                        type="button"
                        onClick={() => onSign(report)}
                        title="Sign off"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <Icons.Check className="h-3.5 w-3.5" />
                        Sign
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onDelete(report)}
                      disabled={report.signed}
                      aria-label="Delete report"
                      title={report.signed ? "Signed reports are locked" : "Delete report"}
                      className="rounded-md p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-500 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                    >
                      <Icons.Trash className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ================================================================== */
/*  Reports landing + wizard host                                      */
/* ================================================================== */
export default function ReportsView({ boards = [], userkey }) {
  const [ask, confirmDialog] = useConfirm();
  const { userid } = useTheme();
  const [tab, setTab] = useState("reports"); // reports | traceability | analytics | history
  const [mode, setMode] = useState("list"); // "list" | "create"
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [zipping, setZipping] = useState(false);
  const [signFor, setSignFor] = useState(null);
  const [openMenuKey, setOpenMenuKey] = useState(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}reports`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setReports(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError(e.message || "Failed to load reports.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const deleteReport = async (report) => {
    const ok = await ask({ title: "Delete report", message: `Delete report "${report.name}"? This cannot be undone.` });
    if (!ok) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}reports/${encodeURIComponent(report.reportkey)}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(report.reportkey);
        return next;
      });
      await loadReports();
    } catch (e) {
      alert(`Could not delete report: ${e.message}`);
    }
  };

  const toggleSelect = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  // Select/deselect every currently shown (filtered) report.
  const toggleSelectAll = (rows) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = rows.length > 0 && rows.every((r) => next.has(r.reportkey));
      if (allOn) rows.forEach((r) => next.delete(r.reportkey));
      else rows.forEach((r) => next.add(r.reportkey));
      return next;
    });

  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Single report → download in the chosen format (pdf | json | html).
  const exportReport = async (report, fmt) => {
    const ext = { pdf: "pdf", json: "json", html: "html" }[fmt] || "pdf";
    try {
      const res = await fetch(
        `${API_BASE_URL}reports/${encodeURIComponent(report.reportkey)}/${fmt}?download=true`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      saveBlob(blob, `${report.name || "report"}.${ext}`);
    } catch (e) {
      alert(`Could not export report: ${e.message}`);
    }
  };

  // Multiple reports → one zip of PDFs.
  const downloadSelected = async () => {
    if (selected.size === 0) return;
    setZipping(true);
    try {
      const res = await fetch(`${API_BASE_URL}reports/download-zip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportkeys: Array.from(selected) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      saveBlob(blob, "reports.zip");
    } catch (e) {
      alert(`Could not download reports: ${e.message}`);
    } finally {
      setZipping(false);
    }
  };

  if (mode === "create") {
    return (
      <CreateReportWizard
        userkey={userkey}
        onCancel={() => setMode("list")}
        onCreated={() => {
          setMode("list");
          loadReports();
        }}
      />
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? reports.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.description || "").toLowerCase().includes(q) ||
          (r.board_name || "").toLowerCase().includes(q)
      )
    : reports;

  const TABS = [
    { id: "reports", label: "Reports", icon: Icons.Report },
    { id: "traceability", label: "Traceability", icon: Icons.List },
    { id: "analytics", label: "Analytics", icon: Icons.Chart },
    { id: "history", label: "History", icon: Icons.Clock },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Results, Reporting &amp; Analytics
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Generate and sign reports, trace requirements, and analyse results.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "reports" && (
            <button
              type="button"
              onClick={() => setMode("create")}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800"
            >
              <Icons.Plus className="h-4 w-4" />
              Create report
            </button>
          )}
          <HelpButton screen="reports" />
        </div>
      </div>

      {/* Tab bar */}
      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const TabIcon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={[
                "-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition",
                active
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-slate-500 hover:text-slate-800",
              ].join(" ")}
            >
              <TabIcon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "traceability" && <TraceabilityView boards={boards} userkey={userkey} />}
      {tab === "analytics" && <AnalyticsView boards={boards} />}
      {tab === "history" && <HistoryView boards={boards} />}

      {tab === "reports" && (
        <>

      {/* Bulk action bar — appears when one or more reports are selected. */}
      {selected.size > 0 && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
          <span className="text-sm font-medium text-brand-800">
            {selected.size} report{selected.size > 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              className="rounded-lg border border-brand-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={downloadSelected}
              disabled={zipping}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {zipping ? (
                <>
                  <Icons.Spinner className="h-4 w-4 animate-spin" />
                  Preparing…
                </>
              ) : (
                <>
                  <Icons.Download className="h-4 w-4" />
                  Download selected ({selected.size})
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {reports.length > 0 && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reports…"
              className="w-full rounded-lg border border-slate-300 bg-card py-2 pl-9 pr-9 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <Icons.X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
          <Icons.Spinner className="h-5 w-5 animate-spin" />
          Loading reports…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-card px-6 py-16 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
            <Icons.Report className="h-6 w-6" />
          </div>
          <h3 className="mt-4 font-semibold text-slate-800">No reports yet</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
            Create your first report by selecting a test suite and choosing which test
            cases to include.
          </p>
          <button
            type="button"
            onClick={() => setMode("create")}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800"
          >
            <Icons.Plus className="h-4 w-4" />
            Create report
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-card px-6 py-12 text-center">
          <p className="text-sm text-slate-500">
            No reports match <span className="font-medium text-slate-700">"{query}"</span>.
          </p>
        </div>
      ) : (
        <ReportsTable
          reports={filtered}
          selected={selected}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={() => toggleSelectAll(filtered)}
          onExport={exportReport}
          onSign={(report) => setSignFor(report)}
          onDelete={deleteReport}
          openMenuKey={openMenuKey}
          setOpenMenuKey={setOpenMenuKey}
        />
      )}
        </>
      )}

      {signFor && (
        <SignReportModal
          report={signFor}
          defaultUserid={userid}
          onClose={() => setSignFor(null)}
          onSigned={() => {
            setSignFor(null);
            loadReports();
          }}
        />
      )}
      {/* The questions handlers ask before they act (useConfirm). */}
      {confirmDialog}
    </div>
  );
}
